import React from "react";
import useScrollReveal from "../../hooks/useScrollReveal";
import CommitmentIcon from "../../images/handshake_thick.png";
import DifferentiationIcon from "../../images/artificial-intelligence_thick.png";

export default function ValuesSection() {
  const [ref, isVisible] = useScrollReveal();

  const values = [
    {
      title: "Trustworthy",
      description: "Delivering clinical transparency, reliable stability testing, and complete compliance to regulatory standards.",
      // Award Ribbon Icon
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="9" r="6" />
          <path d="M9 14.2L6 21.5l3.5-1.5 3.5 1.5-1.5-3.5" />
          <path d="M15 14.2l3 7.3-3.5-1.5-3.5 1.5 1.5-3.5" />
          <path d="M12 6.5l.8 1.6 1.8.2-1.3 1.3.3 1.8-1.6-.9-1.6.9.3-1.8-1.3-1.3 1.8-.2z" fill="currentColor" />
        </svg>
      )
    },
    {
      title: "Commitment",
      description: "Dedicated to meeting project timelines, optimizing scale-up cost, and achieving First-to-Market targets for partners.",
      // Handshake Icon
      icon: (
        <div 
          className="w-10 h-10 bg-current transition-colors duration-300"
          style={{
            maskImage: `url(${CommitmentIcon})`,
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
            WebkitMaskImage: `url(${CommitmentIcon})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain"
          }}
        />
      )
    },
    {
      title: "Quality iN Design",
      description: "Laying deep engineering foundations at the pre-formulation phase to build stable formulations and robust processes.",
      // Glowing Lightbulb Icon
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .5 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
          <path d="M9 18h6" />
          <path d="M10 21h4" />
          <line x1="12" y1="2" x2="12" y2="4" />
          <line x1="4.9" y1="4.9" x2="6.3" y2="6.3" />
          <line x1="19.1" y1="4.9" x2="17.7" y2="6.3" />
          <line x1="2" y1="12" x2="4" y2="12" />
          <line x1="20" y1="12" x2="22" y2="12" />
          <line x1="6.3" y1="17.7" x2="4.9" y2="19.1" />
          <line x1="17.7" y1="17.7" x2="19.1" y2="19.1" />
        </svg>
      )
    },
    {
      title: "Differentiation",
      description: "Leveraging solvent-free technologies and Design of Experiment (DoE) studies to create unique IP assets and formulations.",
      // Face profile with Brain Network Icon
      icon: (
        <div 
          className="w-10 h-10 bg-current transition-colors duration-300"
          style={{
            maskImage: `url(${DifferentiationIcon})`,
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
            WebkitMaskImage: `url(${DifferentiationIcon})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain"
          }}
        />
      )
    },
    {
      title: "Innovation",
      description: "Fostering collaboration with leading global academic institutes and NCE companies to develop innovative drug therapies.",
      // Shining Sun Icon
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      )
    }
  ];

  return (
    <section 
      ref={ref}
      id="values" 
      className={`py-24 bg-white relative transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          <span className="text-brand-teal font-bold text-xs uppercase tracking-widest block">
            Values
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-brand-blue tracking-tight">
            Our Core Principles
          </h2>
          <div className="h-1 w-20 bg-brand-teal mx-auto rounded-full" />
          <p className="text-slate-500 text-sm md:text-base font-light leading-relaxed">
            The foundation of our actions and the driving force behind our commitment to bringing high-quality, affordable healthcare solutions to patients.
          </p>
        </div>

        {/* Minimalist Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {values.map((val) => (
            <div
              key={val.title}
              className="group flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 hover:border-brand-teal/20 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="w-16 h-16 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-6 group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300">
                {val.icon}
              </div>
              <h3 className="font-bold text-lg text-brand-blue mb-2 group-hover:text-brand-teal transition-colors">
                {val.title}
              </h3>
              <p className="text-slate-500 text-xs md:text-sm leading-relaxed font-light">
                {val.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
